import React from 'react';

import { Gateway } from 'types/gateway';

import { useGateways } from 'gateways/hooks/useGateways';

import { Button } from 'components/Button/Button';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { Radio, RadioOption, RadioSelectOption } from 'components/Radio/Radio';
import { Text } from 'components/Text/Text';

import { SourceCamera, SourceConfig } from './types';

type SourceCameraListProps = {
  cameras: SourceCamera[];
  config: SourceConfig;
  quality: string;
  onQualityChange: (quality: string) => void;
  gateway?: Gateway;
  gatewayId?: string;
  onGatewayIdChange: (gatewayId: string | undefined) => void;
  onImport: (cameras: SourceCamera[]) => void;
  isImporting: boolean;
  isFetching: boolean;
  importProgress: { done: number; total: number } | null;
};

function matchesSearch(camera: SourceCamera, query: string): boolean {
  const q = query.toLowerCase();
  return (
    camera.name.toLowerCase().includes(q) ||
    camera.location.toLowerCase().includes(q) ||
    (camera.model?.toLowerCase().includes(q) ?? false) ||
    (camera.serial?.toLowerCase().includes(q) ?? false) ||
    (camera.mac?.toLowerCase().includes(q) ?? false) ||
    (camera.manufacturer?.toLowerCase().includes(q) ?? false) ||
    (camera.ip?.toLowerCase().includes(q) ?? false)
  );
}

export function SourceCameraList({
  cameras,
  config,
  quality,
  onQualityChange,
  gateway,
  gatewayId,
  onGatewayIdChange,
  onImport,
  isImporting,
  isFetching,
  importProgress,
}: SourceCameraListProps) {
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [search, setSearch] = React.useState('');

  const { data: gateways } = useGateways({ params: { limit: 200 } });
  const gatewayOptions = gateways?.data.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const filteredCameras = search.trim()
    ? cameras.filter((cam) => matchesSearch(cam, search.trim()))
    : cameras;

  const allSelected =
    filteredCameras.length > 0 &&
    filteredCameras.every((cam) => selectedIds.has(cam.id));
  const someSelected =
    filteredCameras.some((cam) => selectedIds.has(cam.id)) && !allSelected;

  function toggleAll() {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const cam of filteredCameras) {
          next.delete(cam.id);
        }
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const cam of filteredCameras) {
          next.add(cam.id);
        }
        return next;
      });
    }
  }

  function toggleCamera(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleImport() {
    const selected = cameras.filter((cam) => selectedIds.has(cam.id));
    onImport(selected);
  }

  // Determine which columns have data
  const hasLocation = cameras.some((cam) => cam.location);
  const hasModel = cameras.some((cam) => cam.model);
  const hasSerial = cameras.some((cam) => cam.serial);
  const hasMac = cameras.some((cam) => cam.mac);
  const hasIp = cameras.some((cam) => cam.ip);
  const hasManufacturer = cameras.some((cam) => cam.manufacturer);

  return (
    <div className="flex flex-col gap-[var(--size-md)]">
      <div className="flex flex-col gap-2">
        {!gateway && (
          <Field
            label="Access"
            id="import-access"
            info={
              <>
                Global streams are accessible by any gateway in your workspace.
                <br />
                Select a gateway if the streams are accessible only through a
                specific gateway.
              </>
            }
          >
            <Radio
              id="import-access"
              value={gatewayId ?? 'global'}
              onChange={(value) =>
                onGatewayIdChange(
                  value === 'global' ? undefined : String(value)
                )
              }
            >
              <RadioOption value="global" icon="global">
                Global
              </RadioOption>
              <RadioSelectOption icon="gateway" options={gatewayOptions}>
                Gateway
              </RadioSelectOption>
            </Radio>
          </Field>
        )}

        {config.qualityOptions && (
          <Field label="Quality" id="import-quality">
            <Radio
              id="import-quality"
              value={quality}
              onChange={(value) => onQualityChange(String(value))}
            >
              {config.qualityOptions.map((opt) => (
                <RadioOption key={opt.value} value={opt.value}>
                  {opt.label}
                </RadioOption>
              ))}
            </Radio>
          </Field>
        )}
      </div>

      <Input
        id="camera-search"
        placeholder="Search cameras..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Text size="xsmall">
        {search.trim()
          ? `${filteredCameras.length} of ${cameras.length} cameras`
          : `${cameras.length} camera${cameras.length !== 1 ? 's' : ''} found`}
        {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
      </Text>

      <div className="relative border border-[hsl(var(--ui-separator))] rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
        {(isFetching || isImporting) && (
          <div className="absolute inset-0 flex items-center justify-center bg-[hsl(var(--ui-bg)/0.7)] z-5">
            <Loader
              text={isImporting ? 'Importing streams...' : 'Loading cameras...'}
              size="small"
            />
          </div>
        )}
        <table
          className={`w-full border-0 border-collapse table-auto ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <thead className="border-b border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] sticky top-0 z-3">
            <tr>
              <Text size="xxsmall" asChild>
                <th className="relative p-3 pl-[var(--size-md)] font-[var(--fw-display)] text-left whitespace-nowrap w-[1%]">
                  <Checkbox
                    id="select-all-cameras"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    disabled={isImporting}
                  />
                </th>
              </Text>
              <Text size="xxsmall" asChild>
                <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                  Name
                </th>
              </Text>
              {hasLocation && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    Location
                  </th>
                </Text>
              )}
              {hasIp && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    IP
                  </th>
                </Text>
              )}
              {hasManufacturer && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    Manufacturer
                  </th>
                </Text>
              )}
              {hasModel && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    Model
                  </th>
                </Text>
              )}
              {hasSerial && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    Serial
                  </th>
                </Text>
              )}
              {hasMac && (
                <Text size="xxsmall" asChild>
                  <th className="relative p-3 font-[var(--fw-display)] text-left whitespace-nowrap">
                    MAC
                  </th>
                </Text>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredCameras.map((camera) => (
              <tr
                key={camera.id}
                className={`relative transition-[background-color] duration-100 ease-in-out cursor-pointer [&:not(:last-child)]:border-b [&:not(:last-child)]:border-[hsl(var(--ui-separator))] hover:bg-[hsl(var(--ui-bg))] focus-within:bg-[hsl(var(--ui-bg))] ${isImporting ? 'cursor-default pointer-events-none' : ''}`}
                onClick={() => !isImporting && toggleCamera(camera.id)}
              >
                <td
                  className="relative p-3 pl-[var(--size-md)] text-[var(--text-sm)] w-[1%] whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    id={`camera-${camera.id}`}
                    checked={selectedIds.has(camera.id)}
                    onChange={() => toggleCamera(camera.id)}
                    disabled={isImporting}
                  />
                </td>
                <td className="relative p-3 text-[var(--text-sm)]">
                  {camera.name}
                </td>
                {hasLocation && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.location}
                  </td>
                )}
                {hasIp && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.ip}
                  </td>
                )}
                {hasManufacturer && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.manufacturer}
                  </td>
                )}
                {hasModel && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.model}
                  </td>
                )}
                {hasSerial && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.serial}
                  </td>
                )}
                {hasMac && (
                  <td className="relative p-3 text-[var(--text-sm)]">
                    {camera.mac}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-[var(--size-sm)]">
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={selectedIds.size === 0}
          loading={isImporting}
        >
          {isImporting && importProgress
            ? `Importing ${importProgress.done}/${importProgress.total}...`
            : `Import ${selectedIds.size} stream${selectedIds.size !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
